<template>
  <div class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="mb-6 flex justify-between items-center">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">My Account</h1>
      <div class="flex gap-4">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToAccount=true"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <button
        v-if="!isEditing"
        @click="startEditing"
        class="btn bg-blue-600 text-white hover:bg-blue-700"
      >
        Edit Profile
      </button>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <HowToAccount v-if="showHowToAccount"
      :external-navigation="true"
      :show-how-to-account="showHowToAccount"
      @show-how-to-close="handleShowHowToAccount"
    />

    <!-- Success Message -->
    <div
      v-if="successMessage"
      class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Success! </strong>
      <span class="block sm:inline">{{ successMessage }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
      Loading account details...
    </div>

    <!-- Account Details -->
    <div v-if="!isLoading && !error && account" class="space-y-6">
      <!-- Basic Information Card -->
      <div class="glass-card p-6">
        <h2 class="text-xl font-semibold mb-4">Basic Information</h2>
        <form
          v-if="isEditing"
          @submit.prevent="handleSubmit"
          class="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label class="block text-sm text-gray-600 dark:text-white">Username</label>
            <input
              v-model="editForm.username"
              type="text"
              class="px-3 py-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-white">Organisation</label>
            <p class="mt-1 text-gray-900 dark:text-white">{{ orgName || "N/A" }}</p>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-white">First Name</label>
            <input
              v-model="editForm.first_name"
              type="text"
              class="px-3 py-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-white">Last Name</label>
            <input
              v-model="editForm.last_name"
              type="text"
              class="px-3 py-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div class="md:col-span-2 flex gap-4 mt-4">
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              :disabled="isSaving"
            >
              {{ isSaving ? "Saving..." : "Save Changes" }}
            </button>
            <button
              type="button"
              @click="cancelEditing"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </form>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 dark:text-white">
          <div>
            <p class="text-sm text-gray-600 dark:text-white">Username</p>
            <p class="font-medium">{{ account.username }}</p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">Organisation</p>
            <p class="font-medium">{{ orgName || "N/A" }}</p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">First Name</p>
            <p class="font-medium">{{ account.first_name || "N/A" }}</p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">Last Name</p>
            <p class="font-medium">{{ account.last_name || "N/A" }}</p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">Member Since</p>
            <p class="font-medium">{{ formatDateTime(account.date_joined) }}</p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">Last Login</p>
            <p class="font-medium">{{ formatDateTime(account.last_login) }}</p>
          </div>
        </div>
      </div>

      <!-- Account Status Card -->
      <div class="glass-card p-6">
        <h2 class="text-xl font-semibold mb-4">Account Status</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600 dark:text-white">Status</p>
            <p
              :class="[
                'font-medium',
                account.is_active ? 'text-green-600 dark:text-green-500' : 'text-red-600',
              ]"
            >
              {{ account.is_active ? "Active" : "Inactive" }}
            </p>
          </div>

          <div>
            <p class="text-sm text-gray-600 dark:text-white">Account Type</p>
            <p class="font-medium">
              {{
                account.is_superuser
                  ? "Superuser"
                  : account.is_staff
                  ? "Staff"
                  : "Regular User"
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Notification Preferences Card -->
      <div
        v-if="hasCommitPermission"
        class="glass-card p-6"
      >
        <h2 class="text-xl font-semibold mb-4">Notification Preferences</h2>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-200">Email me when changes need committing</p>
            <p class="text-sm font-normal text-gray-600 dark:text-gray-300 mt-0.5">
              {{ account.receive_commit_notifications_updated_at
                ? `Last Updated ${formatDateTime(account.receive_commit_notifications_updated_at)}`
                : 'Never changed, currently using the default (on)' }}
            </p>
          </div>
          <div class="relative inline-block w-12 ml-2 align-middle select-none transition duration-200 ease-in flex-shrink-0">
            <input
              id="receive_commit_notifications"
              type="checkbox"
              v-model="account.receive_commit_notifications"
              @change="promptNotificationChange"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
            />
            <label
              for="receive_commit_notifications"
              class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
            ></label>
          </div>
        </div>
      </div>

      <!-- Permissions Card -->
      <div class="glass-card p-6">
        <button
          type="button"
          class="flex w-full items-center justify-between bg-transparent border-none outline-none focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2 cursor-pointer mb-4 transition-colors duration-150"
          @click="permissionsExpanded = !permissionsExpanded"
        >
          <h2 class="text-xl font-semibold">Permissions</h2>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200"
            :class="{ 'rotate-180': permissionsExpanded }"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div v-show="permissionsExpanded" class="mt-4 bg-gray-50 rounded-md p-4 dark:bg-gray-800">
          <div v-if="account.permissions && account.permissions.length">
            <ul class="list-disc list-inside space-y-1">
              <li
                v-for="permission in account.permissions"
                :key="permission"
                class="text-gray-700 dark:text-white dark:font-medium"
              >
                {{ formatPermissionName(permission) }}
              </li>
            </ul>
          </div>
          <p v-else class="text-gray-700">No specific permissions assigned.</p>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="glass-card p-6">
        <h2 class="text-xl font-semibold mb-4">Account Actions</h2>
        <div class="flex flex-col sm:flex-row gap-4">
          <button
            @click="showPasswordModal = true"
            class="btn inline-flex items-center justify-center shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Change Password
          </button>
          <button
            @click="showDeleteModal = true"
            class="btn inline-flex items-center justify-center  text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>

    <!-- Password Change Modal -->
    <div
      v-if="showPasswordModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="glass-card p-8 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Change Password</h3>
        <form @submit.prevent="handlePasswordChange">
          <div class="space-y-4">
            <div class="dark:text-gray-50">
              <ul class="list-disc list-inside space-y-1">
                <li>Your password cannot be too similar to your other personal information.</li>
                <li>Your password must contain at least 8 characters.</li>
                <li>Your password cannot be a commonly used password.</li>
                <li>Your password cannot be entirely numeric.</li>
              </ul>
            </div>
            <div class="text-center space-y-4">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-50"
                  >Current Password</label
                >
                <input
                  v-model="passwordForm.old_password"
                  type="password"
                  required
                  :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['old_password']
                      ? 'ring-2 ring-red-500  focus:ring-red-500'
                      : 'focus:ring-blue-500'
                    ]"
                  />
                  <FieldError :message="fieldErrors['old_password']" />
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-50">New Password</label>
                <input
                  v-model="passwordForm.new_password"
                  type="password"
                  required
                  :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['new_password']
                      ? 'ring-2 ring-red-500  focus:ring-red-500'
                      : 'focus:ring-blue-500'
                    ]"
                  />
                  <FieldError :message="fieldErrors['new_password']" />
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-50"
                  >Confirm New Password</label
                >
                <input
                  v-model="passwordForm.confirm_password"
                  type="password"
                  required
                  :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['confirm_password']
                      ? 'ring-2 ring-red-500  focus:ring-red-500'
                      : 'focus:ring-blue-500'
                    ]"
                  />
                  <FieldError :message="fieldErrors['confirm_password']" />
              </div>
            </div>
          </div>
            
          <div class="mt-6 flex gap-4">
            <button
              type="button"
              @click="cancelPasswordChange"
              class="btn bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn bg-blue-600 text-white rounded-md hover:bg-blue-700"
              :disabled="isChangingPassword"
            >
              {{ isChangingPassword ? "Changing..." : "Change Password" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Notification Preference Confirmation Modal -->
    <div
      v-if="showNotificationConfirmModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="glass-card p-8 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">
          {{ previousNotificationValue ? 'Disable commit notifications' : 'Enable commit notifications' }}
        </h3>
        <p class="text-gray-600 dark:text-gray-50 mb-6">
          {{ previousNotificationValue
            ? 'You will no longer receive emails when saved changes require committing to production.'
            : 'You will receive an email each time a saved change requires committing to production.' }}
        </p>
        <div class="flex justify-center gap-4">
          <button @click="confirmNotificationChange" class="btn-primary">
            Confirm
          </button>
          <button @click="cancelNotificationChange" class="btn-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Account Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="glass-card p-8 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Delete Account</h3>
        <p class="text-gray-600 dark:text-gray-50 mb-6">
          Are you sure you want to delete your account? This action cannot be
          undone.
        </p>
        <div class="flex gap-4">
          <button
            @click="handleDeleteAccount"
            :disabled="isDeleting"
            class="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {{ isDeleting ? "Deleting..." : "Delete Account" }}
          </button>
          <button
            @click="showDeleteModal = false"
            class="btn bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { useStore } from "@/store";
import {
  getMyAccount,
  updateAccount,
  changePassword,
  deleteAccount,
} from "@/services/accountService";
import { getAllOrgs } from "@/services/orgService";
import FieldError from "@/components/FieldError.vue";
import HowToAccount from "./HowToAccount.vue";

export default {
  name: "MyAccount",
  components: {
    FieldError,
    HowToAccount
  },
  setup() {
    const hasCommitPermission = computed(() =>
      useStore.isStaff() ||
      useStore.isSuperuser() ||
      useStore.hasPermission("Can commit changes to production")
    );
    const account = ref(null);
    const error = ref(null);
    const successMessage = ref(null);
    const isLoading = ref(false);
    const isEditing = ref(false);
    const isSaving = ref(false);
    const isChangingPassword = ref(false);
    const showDeleteModal = ref(false);
    const showPasswordModal = ref(false);
    const isDeleting = ref(false);
    const fieldErrors = ref({})
    const orgName = ref("")
    const showHowToAccount = ref(false)
    const showNotificationConfirmModal = ref(false)
    const previousNotificationValue = ref(null)
    const permissionsExpanded = ref(false)

    const editForm = ref({
      username: "",
      first_name: "",
      last_name: "",
    });

    const passwordForm = ref({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });

    const fetchAccountDetails = async () => {
      isLoading.value = true;
      try {
        account.value = await getMyAccount();
        error.value = null;
        await fetchOrgs();
      } catch (err) {
        console.error("Error fetching account details:", err);
        error.value = "Failed to fetch account details. Please try again later.";
        account.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const fetchOrgs = async() => {
      try {
        const response = await getAllOrgs();
        orgName.value = response.find(org => org.id == account.value.org)?.name;
      } catch (error) {
        console.error(error)
      }
    };

    const handleShowHowToAccount = (value) => {
      showHowToAccount.value = value;
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "Never";
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const startEditing = () => {
      editForm.value = {
        username: account.value.username,
        first_name: account.value.first_name || "",
        last_name: account.value.last_name || "",
      };
      isEditing.value = true;
    };

    const cancelPasswordChange = () =>{
      fieldErrors.value = {};
      showPasswordModal.value = false;
      passwordForm.value = {
        old_password: "",
        new_password: "",
        confirm_password: "",
      }
    }

    const cancelEditing = () => {
      isEditing.value = false;
      error.value = null;
    };

    const handleSubmit = async () => {
      isSaving.value = true;
      error.value = null;
      successMessage.value = null;
      try {
        const updated = await updateAccount(editForm.value);
        account.value = { ...account.value, ...updated };
        isEditing.value = false;
        successMessage.value = "Profile updated successfully";
      } catch (err) {
        error.value =
          err.response?.data?.detail ||
          "Failed to update account. Please try again.";
      } finally {
        isSaving.value = false;
      }
    };

    const handlePasswordChange = async () => {
      fieldErrors.value = {};
      if (
        passwordForm.value.new_password !== passwordForm.value.confirm_password
      ) {
        const error = "New passwords do not match";
        fieldErrors.value = {...fieldErrors.value, "confirm_password": error}
        return;
      }
      fieldErrors.value = {};
      isChangingPassword.value = true;
      error.value = null;
      successMessage.value = null;
      try {
        await changePassword({
          old_password: passwordForm.value.old_password,
          new_password: passwordForm.value.new_password,
        });
        showPasswordModal.value = false;
        successMessage.value = "Password changed successfully";
        // Reset password form
        passwordForm.value = {
          old_password: "",
          new_password: "",
          confirm_password: "",
        };
         
      } catch (err) {
      
        fieldErrors.value = {}; 
        const errData = err?.response?.data;

        for (const element in errData){
          const fieldError = errData?.[element];
          if (fieldError?.[0]) {
            console.log(element)
            if (element == 'new_password'){
              const errorString = fieldError[0];
              const errors = JSON.parse(errorString.replace(/'/g, '"'));
              fieldErrors.value[element] = errors.join('. ');
              
            }
            else fieldErrors.value[element] = fieldError[0];
          }
        }  
      } finally {
        isChangingPassword.value = false;
      }
    };

    const handleDeleteAccount = async () => {
      error.value = null;
      isDeleting.value = true;
      try {
        await deleteAccount();
        localStorage.removeItem("token");
        window.location.href = "/login";
      } catch (err) {
        console.error("Delete account error:", err);
        error.value =
          err.response?.data?.error ||
          "Failed to delete account. Please try again.";
        showDeleteModal.value = false;
      } finally {
        isDeleting.value = false;
      }
    };

    const promptNotificationChange = () => {
      previousNotificationValue.value = !account.value.receive_commit_notifications;
      showNotificationConfirmModal.value = true;
    };

    const cancelNotificationChange = () => {
      account.value.receive_commit_notifications = previousNotificationValue.value;
      showNotificationConfirmModal.value = false;
    };

    const saveNotificationPreference = async () => {
      try {
        const updated = await updateAccount({ receive_commit_notifications: account.value.receive_commit_notifications });
        account.value.receive_commit_notifications_updated_at = updated.receive_commit_notifications_updated_at;
        successMessage.value = "Notification preference saved";
      } catch (err) {
        account.value.receive_commit_notifications = previousNotificationValue.value;
        error.value = "Failed to save notification preference. Please try again.";
      }
    };

    const confirmNotificationChange = async () => {
      showNotificationConfirmModal.value = false;
      await saveNotificationPreference();
    };

    const clearMessages = () => {
      error.value = null;
      successMessage.value = null;
    };

    // Set up automatic message clearing
    const clearMessageTimeout = ref(null);
    const setClearMessageTimeout = () => {
      if (clearMessageTimeout.value) {
        clearTimeout(clearMessageTimeout.value);
      }
      clearMessageTimeout.value = setTimeout(() => {
        clearMessages();
      }, 5000); // Messages will clear after 5 seconds
    };


    const formatPermissionName = (codename) => {
      const parts = codename.split(" ");
      if (parts.length >= 2 && parts[parts.length-1].toLowerCase() === "blackbox") {
        return `${parts[0]} ${parts[1]} Endpoint Availability`
      }
     return codename;
    };

    // Watch for changes in error or success messages
    watch([error, successMessage], ([newError, newSuccess]) => {
      if (newError || newSuccess) {
        setClearMessageTimeout();
      }
    });

    // // Clean up on component unmount
    // onUnmounted(() => {
    //   if (clearMessageTimeout.value) {
    //     clearTimeout(clearMessageTimeout.value);
    //   }
    // });

    onMounted(fetchAccountDetails);

    return {
      account,
      error,
      successMessage,
      isLoading,
      isEditing,
      isSaving,
      isChangingPassword,
      isDeleting,
      showDeleteModal,
      showPasswordModal,
      editForm,
      passwordForm,
      formatDateTime,
      startEditing,
      cancelEditing,
      handleSubmit,
      handlePasswordChange,
      handleDeleteAccount,
      clearMessages,
      fieldErrors,
      FieldError,
      cancelPasswordChange,
      formatPermissionName,
      orgName,
      handleShowHowToAccount,
      showHowToAccount,
      hasCommitPermission,
      permissionsExpanded,
      showNotificationConfirmModal,
      previousNotificationValue,
      promptNotificationChange,
      cancelNotificationChange,
      saveNotificationPreference,
      confirmNotificationChange,
    };
  },
};
</script>

<style scoped>
/* Add any component-specific styles here */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

</style>
